import {
  IconBanknote,
  IconListBullets,
  IconImages,
  IconBarsThree,
  IconTable,
  IconH1,
  IconH2,
  IconH3,
  IconNumberedList
} from '@sequencehq/core-components'
import { blocknoteSchema } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/blocknoteSchema'
import { SuggestionItem } from './types'
import { QUOTE_CONTAINER_WIDTH } from '@sequencehq/quote-content'
import { insertOrUpdateBlockForSlashMenu } from '@blocknote/core'

const heading1MenuItem = (editor: typeof blocknoteSchema.BlockNoteEditor) => ({
  title: 'Heading 1',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'heading',
      props: {
        level: 1
      }
    }
    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconH1 />
})

const heading2MenuItem = (editor: typeof blocknoteSchema.BlockNoteEditor) => ({
  title: 'Heading 2',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'heading',
      props: {
        level: 2
      }
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconH2 />
})

const heading3MenuItem = (editor: typeof blocknoteSchema.BlockNoteEditor) => ({
  title: 'Heading 3',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'heading',
      props: {
        level: 3
      }
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconH3 />
})

const paragraphMenuItem = (editor: typeof blocknoteSchema.BlockNoteEditor) => ({
  title: 'Paragraph',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'paragraph'
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconBarsThree />
})

const bulletListMenuItem = (
  editor: typeof blocknoteSchema.BlockNoteEditor
) => ({
  title: 'Bullet list',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'bulletListItem'
    }
    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconListBullets />
})

const numberListMenuItem = (
  editor: typeof blocknoteSchema.BlockNoteEditor
) => ({
  title: 'Numbered list',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'numberedListItem'
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconNumberedList />
})

const imageMenuItem = (editor: typeof blocknoteSchema.BlockNoteEditor) => ({
  title: 'Image',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'image'
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconImages />
})

const phasesSlashMenuItem = (
  editor: typeof blocknoteSchema.BlockNoteEditor,
  addPhaseBlockEnabled: boolean
) => ({
  title: 'Pricing',
  onItemClick: () => {
    if (!addPhaseBlockEnabled) {
      return
    }

    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'phases'
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconBanknote />
})

const createInitialTableCells = ({
  rows,
  columns
}: {
  rows: number
  columns: number
}) => {
  return {
    columnWidths: Array.from({ length: columns }).map(
      () => parseInt(QUOTE_CONTAINER_WIDTH) / columns
    ),
    rows: Array.from({ length: rows }).map(() => ({
      cells: Array.from({ length: columns }).map(() => ({
        type: 'tableCell' as const,
        content: [],
        props: {
          backgroundColor: 'default' as const,
          textColor: 'default' as const,
          textAlignment: 'left' as const
        }
      }))
    }))
  }
}

const tableMenuItem = (editor: typeof blocknoteSchema.BlockNoteEditor) => ({
  title: 'Table',
  onItemClick: () => {
    const block: typeof blocknoteSchema.PartialBlock = {
      type: 'table',
      props: { textColor: 'default' },
      content: {
        type: 'tableContent',
        ...createInitialTableCells({ columns: 3, rows: 3 })
      }
    }

    insertOrUpdateBlockForSlashMenu(editor, block)
  },
  icon: <IconTable />
})

export const getCustomSuggestionMenuItems = (
  editor: typeof blocknoteSchema.BlockNoteEditor,
  addPhaseBlockEnabled: boolean
): SuggestionItem[] => [
  heading1MenuItem(editor),
  heading2MenuItem(editor),
  heading3MenuItem(editor),
  paragraphMenuItem(editor),
  bulletListMenuItem(editor),
  numberListMenuItem(editor),
  phasesSlashMenuItem(editor, addPhaseBlockEnabled),
  imageMenuItem(editor),
  tableMenuItem(editor)
]
